import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useNavigation } from '@react-navigation/native';
import { useTheme, Text } from 'react-native-paper';
import { Dimensions, Platform, View, Pressable } from 'react-native';
import { Plus } from 'lucide-react-native';

import {
  navigationConfig,
  tabBarIcons,
  defaultScreenOptions,
  getRouteTitle,
  shouldShowBackButton,
} from '../config/navigationConfig';
import { useNavigationState } from '../hooks/useNavigationState';

// Screens
import { RecipesScreen } from '../screens/RecipesScreen';
import { RecipeCreateScreen } from '../screens/RecipeCreateScreen';
import { CreatorsScreen } from '../screens/CreatorsScreen';
import { CreatorProfileScreen } from '../screens/CreatorProfileScreen';
import { CreatorRecipesScreen } from '../screens/CreatorRecipesScreen';
import { EquipmentScreen } from '../screens/EquipmentScreen';
import { EquipmentCreateScreen } from '../screens/EquipmentCreateScreen';
import { RoastersScreen } from '../screens/RoastersScreen';
import { FavoritesScreen } from '../screens/FavoritesScreen';
import { EquipmentDetailScreen } from '../screens/EquipmentDetailScreen';
import { DrawerContent } from '../components/ui/DrawerContent';
import { ResponsiveLayout, ResponsiveHeader } from '../components/layout';
import { RecipeDetail } from '../components/recipes/RecipeDetail';
import { JourneyNavigator } from './JourneyNavigator';
import { AIExperienceNavigator } from './AIExperienceNavigator';
import { ProfileScreen } from '../screens/ProfileScreen';
import { EditProfileScreen } from '../screens/EditProfileScreen';
import { MyRecipesScreen } from '../screens/MyRecipesScreen';
import { MyCommentsScreen } from '../screens/MyCommentsScreen';
import { PrivacySecurityScreen } from '../screens/PrivacySecurityScreen';
import { HelpSupportScreen } from '../screens/HelpSupportScreen';
import { SettingsScreen } from '../screens/SettingsScreen';
import { DeleteAccountScreen } from '../screens/DeleteAccountScreen';
import { NotificationSettingsScreen } from '../screens/NotificationSettingsScreen';
import { DashboardTestScreen } from '../screens/DashboardTestScreen';
import { RecommendationsScreen } from '../screens/RecommendationsScreen';
import { TermsAndConditionsScreen } from '../screens/TermsAndConditionsScreen';
import { UserProfileScreen } from '../screens/UserProfileScreen';
import { logger } from '../utils/logger';

// Wrapper component to handle navigation params
function RecipeDetailScreen({ route }: any) {
  const { recipeId } = route.params;
  return <RecipeDetail recipeId={recipeId} />;
}

// Import types
import type { MainTabParamList, RootDrawerParamList } from './types';

// Create navigators
const Tab = createBottomTabNavigator<MainTabParamList>();
const Drawer = createDrawerNavigator<RootDrawerParamList>();
const Stack = createNativeStackNavigator();

// Optimized stack navigators with performance enhancements
function RecipesStack() {
  const theme = useTheme();
  const getScreenOptions = useCallback(
    (routeName: string) => ({
      title: getRouteTitle(routeName),
      header: (props: any) => (
        <ResponsiveHeader
          {...props}
          forceShowBack={shouldShowBackButton(routeName)}
        />
      ),
      headerShown: true,
      lazy: true,
      ...navigationConfig.animations.slide,
    }),
    []
  );

  return (
    <Stack.Navigator
      screenOptions={{
        ...defaultScreenOptions.stack,
        header: (props: any) => <ResponsiveHeader {...props} />,
      }}
    >
      <Stack.Screen
        name='index'
        component={RecipesScreen}
        options={({ navigation }) => ({
          ...getScreenOptions('recipes'),
          headerRight: ({ tintColor }) => (
            <Pressable
              onPress={() => navigation.navigate('createRecipe')}
              accessibilityRole='button'
              accessibilityLabel='Create new recipe'
              hitSlop={12}
              style={{
                padding: 8,
                borderRadius: 8,
              }}
              testID='create-recipe-header-button'
            >
              <Plus color={tintColor || theme.colors.onSurface} size={22} />
            </Pressable>
          ),
        })}
      />
      <Stack.Screen
        name='recipeDetail'
        component={RecipeDetailScreen}
        options={getScreenOptions('recipeDetail')}
      />
      <Stack.Screen
        name='createRecipe'
        component={RecipeCreateScreen}
        options={getScreenOptions('createRecipe')}
      />
      <Stack.Screen
        name='roasters'
        component={RoastersScreen}
        options={getScreenOptions('roasters')}
      />
      <Stack.Screen
        name='userProfile'
        component={UserProfileScreen}
        options={getScreenOptions('userProfile')}
      />
      <Stack.Screen
        name='creatorProfile'
        component={CreatorProfileScreen}
        options={getScreenOptions('creatorProfile')}
      />
    </Stack.Navigator>
  );
}

function EquipmentStack() {
  const theme = useTheme();
  const getScreenOptions = useCallback(
    (routeName: string) => ({
      title: getRouteTitle(routeName),
      header: (props: any) => (
        <ResponsiveHeader
          {...props}
          forceShowBack={shouldShowBackButton(routeName)}
        />
      ),
      headerShown: true,
      lazy: true,
      ...navigationConfig.animations.slide,
    }),
    []
  );

  return (
    <Stack.Navigator
      screenOptions={{
        ...defaultScreenOptions.stack,
        header: (props: any) => <ResponsiveHeader {...props} />,
      }}
    >
      <Stack.Screen
        name='index'
        component={EquipmentScreen}
        options={({ navigation }) => ({
          ...getScreenOptions('equipment'),
          headerRight: ({ tintColor }) => (
            <Pressable
              onPress={() => navigation.navigate('equipmentCreate')}
              accessibilityRole='button'
              accessibilityLabel='Create new equipment'
              hitSlop={12}
              style={{
                padding: 8,
                borderRadius: 8,
              }}
              testID='create-equipment-header-button'
            >
              <Plus color={tintColor || theme.colors.onSurface} size={22} />
            </Pressable>
          ),
        })}
      />
      <Stack.Screen
        name='equipmentDetail'
        component={EquipmentDetailScreen}
        options={getScreenOptions('equipmentDetail')}
      />
      <Stack.Screen
        name='equipmentCreate'
        component={EquipmentCreateScreen}
        options={getScreenOptions('equipmentCreate')}
      />
      <Stack.Screen
        name='userProfile'
        component={UserProfileScreen}
        options={getScreenOptions('userProfile')}
      />
    </Stack.Navigator>
  );
}

function CreatorsStack() {
  const getScreenOptions = useCallback(
    (routeName: string) => ({
      title: getRouteTitle(routeName),
      header: (props: any) => (
        <ResponsiveHeader
          {...props}
          forceShowBack={shouldShowBackButton(routeName)}
        />
      ),
      headerShown: true,
      lazy: true,
      ...navigationConfig.animations.slide,
    }),
    []
  );

  return (
    <Stack.Navigator
      screenOptions={{
        ...defaultScreenOptions.stack,
        header: (props: any) => <ResponsiveHeader {...props} />,
      }}
    >
      <Stack.Screen
        name='index'
        component={CreatorsScreen}
        options={getScreenOptions('creators')}
      />
      <Stack.Screen
        name='creatorProfile'
        component={CreatorProfileScreen}
        options={getScreenOptions('creatorProfile')}
      />
      <Stack.Screen
        name='creatorRecipes'
        component={CreatorRecipesScreen}
        options={({ route }: any) => ({
          ...getScreenOptions('creatorRecipes'),
          title: `${route.params?.creatorName || 'Creator'}'s Recipes`,
        })}
      />
      <Stack.Screen
        name='recipeDetail'
        component={RecipeDetailScreen}
        options={getScreenOptions('recipeDetail')}
      />
      <Stack.Screen
        name='userProfile'
        component={UserProfileScreen}
        options={getScreenOptions('userProfile')}
      />
    </Stack.Navigator>
  );
}

// Journey and AI Experience already have their own navigators with sub-screens
function JourneyStack() {
  return <JourneyNavigator />;
}

function AIExperienceStack() {
  return <AIExperienceNavigator />;
}

function ProfileStack() {
  const getScreenOptions = useCallback(
    (routeName: string) => ({
      title: getRouteTitle(routeName),
      header: (props: any) => (
        <ResponsiveHeader
          {...props}
          forceShowBack={shouldShowBackButton(routeName)}
        />
      ),
      headerShown: true,
      lazy: true,
      ...navigationConfig.animations.slide,
    }),
    []
  );

  return (
    <Stack.Navigator
      initialRouteName='index'
      screenOptions={{
        ...defaultScreenOptions.stack,
        header: (props: any) => <ResponsiveHeader {...props} />,
      }}
    >
      <Stack.Screen
        name='index'
        component={ProfileScreen}
        options={getScreenOptions('profile')}
      />
      <Stack.Screen
        name='editProfile'
        component={EditProfileScreen}
        options={getScreenOptions('editProfile')}
      />
      <Stack.Screen
        name='myRecipes'
        component={MyRecipesScreen}
        options={getScreenOptions('myRecipes')}
      />
      <Stack.Screen
        name='myComments'
        component={MyCommentsScreen}
        options={getScreenOptions('myComments')}
      />
      <Stack.Screen
        name='notificationSettings'
        component={NotificationSettingsScreen}
        options={getScreenOptions('notificationSettings')}
      />
      <Stack.Screen
        name='deleteAccount'
        component={DeleteAccountScreen}
        options={getScreenOptions('deleteAccount')}
      />
      <Stack.Screen
        name='privacySecurity'
        component={PrivacySecurityScreen}
        options={getScreenOptions('privacySecurity')}
      />
      {/* Settings and Help & Support removed - they're top-level routes accessible from sidebar */}
      <Stack.Screen
        name='createRecipe'
        component={RecipeCreateScreen}
        options={getScreenOptions('createRecipe')}
      />
      <Stack.Screen
        name='recipeDetail'
        component={RecipeDetailScreen}
        options={getScreenOptions('recipeDetail')}
      />
      <Stack.Screen
        name='favorites'
        component={FavoritesScreen}
        options={getScreenOptions('favorites')}
      />
      <Stack.Screen
        name='userProfile'
        component={UserProfileScreen}
        options={getScreenOptions('userProfile')}
      />
      <Stack.Screen
        name='creatorProfile'
        component={CreatorProfileScreen}
        options={getScreenOptions('creatorProfile')}
      />
    </Stack.Navigator>
  );
}

/**
 * Optimized NavigationContent with clean URLs and responsive tab bar
 */
export function NavigationContent() {
  const theme = useTheme();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const { activeRoute, setActiveRoute } = useNavigationState();

  // Memoized screen size detection for performance
  // Use width only - standard responsive design approach
  const updateScreenSize = useCallback(() => {
    const { width } = Dimensions.get('window');
    const isSmall = width < navigationConfig.breakpoints.mobile;
    setIsSmallScreen(isSmall);
  }, []);

  useEffect(() => {
    updateScreenSize();
    const subscription = Dimensions.addEventListener(
      'change',
      updateScreenSize
    );
    return () => subscription?.remove();
  }, [updateScreenSize]);

  // Memoized tab navigator configuration - Hides tab bar on large screens
  const tabNavigatorConfig = useMemo(
    () => ({
      screenOptions: ({ route }: { route: { name: string } }) => ({
        tabBarIcon: ({ color, size }: { color: string; size: number }) => {
          const IconComponent =
            tabBarIcons[route.name as keyof typeof tabBarIcons];
          return IconComponent ? (
            <IconComponent size={size} color={color} />
          ) : null;
        },
        tabBarActiveTintColor: theme.colors.primary,
        tabBarInactiveTintColor: theme.colors.onSurfaceVariant,
        tabBarStyle: isSmallScreen
          ? {
              backgroundColor: theme.colors.surface,
              borderTopColor: theme.colors.outline,
              paddingBottom: 8,
              paddingTop: 8,
              height: 60,
            }
          : {
              display: 'none' as const, // Hide tab bar on large screens (≥768px)
            },
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: '500' as const,
        },
        headerShown: false,
        lazy: true,
      }),
    }),
    [theme, isSmallScreen]
  );

  const handleNavigate = useCallback(
    (route: string) => {
      setActiveRoute(route);
    },
    [setActiveRoute]
  );

  // Memoized drawer configuration
  const drawerConfig = useMemo(
    () => ({
      screenOptions: {
        ...defaultScreenOptions.drawer,
        lazy: true,
        ...(Platform.OS === 'web' && {
          swipeEnabled: false,
        }),
      },
      drawerContent: (props: any) => <DrawerContent {...props} />,
    }),
    []
  );

  // Memoized Tab Navigator component for small screens
  const TabNavigatorContent = useCallback(
    () => (
      <Tab.Navigator {...tabNavigatorConfig}>
        <Tab.Screen
          name='recipes'
          component={RecipesStack}
          options={{ tabBarLabel: getRouteTitle('recipes') }}
        />
        <Tab.Screen
          name='creators'
          component={CreatorsStack}
          options={{ tabBarLabel: getRouteTitle('creators') }}
        />
        <Tab.Screen
          name='equipment'
          component={EquipmentStack}
          options={{ tabBarLabel: getRouteTitle('equipment') }}
        />
        <Tab.Screen
          name='profile'
          component={ProfileStack}
          options={{ tabBarLabel: getRouteTitle('profile') }}
        />
      </Tab.Navigator>
    ),
    [tabNavigatorConfig]
  );

  // Wrapper component that renders content with persistent bottom tabs (small screens only)
  const ScreenWithBottomTabs = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    const navigation = useNavigation<any>();
    logger.info(isSmallScreen);
    return (
      <View style={{ flex: 1 }}>
        <View style={{ flex: 1 }}>{children}</View>
        {/* Persistent bottom tab bar - only on small screens */}
        {isSmallScreen && (
          <View
            style={{
              backgroundColor: theme.colors.surface,
              borderTopWidth: 1,
              borderTopColor: theme.colors.outline,
              paddingBottom: 8,
              paddingTop: 8,
              height: 60,
              flexDirection: 'row',
            }}
          >
            {navigationConfig.mainTabRoutes.map(route => {
              const IconComponent = tabBarIcons[route];
              return (
                <Pressable
                  key={route}
                  style={{
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onPress={() => {
                    navigation.navigate('home', {
                      screen: route,
                    });
                  }}
                >
                  <IconComponent
                    size={24}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: '500',
                      color: theme.colors.onSurfaceVariant,
                      marginTop: 4,
                    }}
                  >
                    {getRouteTitle(route)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  // Mobile navigator - Direct screen rendering for clean URLs
  const MobileNavigator = useCallback(
    () => (
      <Drawer.Navigator {...drawerConfig}>
        <Drawer.Screen name='home' options={{ headerShown: false }}>
          {TabNavigatorContent}
        </Drawer.Screen>

        {/* Direct screen components for clean URLs (no /index) */}
        <Drawer.Screen
          name='recommendations'
          options={{
            title: getRouteTitle('recommendations'),
            header: (props: any) => <ResponsiveHeader {...props} />,
            headerShown: true,
          }}
        >
          {() => (
            <ScreenWithBottomTabs>
              <RecommendationsScreen />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>

        <Drawer.Screen
          name='termsAndConditions'
          options={{
            title: getRouteTitle('termsAndConditions'),
            header: (props: any) => <ResponsiveHeader {...props} />,
            headerShown: true,
          }}
        >
          {() => (
            <ScreenWithBottomTabs>
              <TermsAndConditionsScreen />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>

        <Drawer.Screen name='journey' options={{ headerShown: false }}>
          {() => (
            <ScreenWithBottomTabs>
              <JourneyStack />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>

        <Drawer.Screen name='aiExperience' options={{ headerShown: false }}>
          {() => (
            <ScreenWithBottomTabs>
              <AIExperienceStack />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>

        <Drawer.Screen
          name='settings'
          options={{
            title: getRouteTitle('settings'),
            header: (props: any) => <ResponsiveHeader {...props} />,
            headerShown: true,
          }}
        >
          {() => (
            <ScreenWithBottomTabs>
              <SettingsScreen />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>

        <Drawer.Screen
          name='helpSupport'
          options={{
            title: getRouteTitle('helpSupport'),
            header: (props: any) => <ResponsiveHeader {...props} />,
            headerShown: true,
          }}
        >
          {() => (
            <ScreenWithBottomTabs>
              <HelpSupportScreen />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>

        <Drawer.Screen name='dashboardTest' options={{ headerShown: false }}>
          {() => (
            <ScreenWithBottomTabs>
              <DashboardTestScreen />
            </ScreenWithBottomTabs>
          )}
        </Drawer.Screen>
      </Drawer.Navigator>
    ),
    [drawerConfig, TabNavigatorContent, isSmallScreen]
  );

  // Large screen Tab Navigator (with hidden tab bar)
  const LargeScreenTabNavigator = useCallback(
    () => (
      <Tab.Navigator {...tabNavigatorConfig}>
        <Tab.Screen
          name='recipes'
          component={RecipesStack}
          options={{ tabBarLabel: getRouteTitle('recipes') }}
        />
        <Tab.Screen
          name='creators'
          component={CreatorsStack}
          options={{ tabBarLabel: getRouteTitle('creators') }}
        />
        <Tab.Screen
          name='equipment'
          component={EquipmentStack}
          options={{ tabBarLabel: getRouteTitle('equipment') }}
        />
        <Tab.Screen
          name='profile'
          component={ProfileStack}
          options={{ tabBarLabel: getRouteTitle('profile') }}
        />
      </Tab.Navigator>
    ),
    [tabNavigatorConfig]
  );

  // Core navigator - Direct screen rendering for clean URLs
  const CoreNavigator = useCallback(() => {
    if (isSmallScreen) {
      return <MobileNavigator />;
    }

    // Large screens: Direct screen rendering, no bottom tabs
    return (
      <Stack.Navigator
        screenOptions={{
          ...defaultScreenOptions.stack,
          header: (props: any) => <ResponsiveHeader {...props} />,
        }}
      >
        <Stack.Screen name='home' options={{ headerShown: false }}>
          {LargeScreenTabNavigator}
        </Stack.Screen>

        <Stack.Screen
          name='recommendations'
          component={RecommendationsScreen}
          options={{
            title: getRouteTitle('recommendations'),
            headerShown: true,
          }}
        />

        <Stack.Screen
          name='termsAndConditions'
          component={TermsAndConditionsScreen}
          options={{
            title: getRouteTitle('termsAndConditions'),
            headerShown: true,
          }}
        />

        <Stack.Screen
          name='journey'
          component={JourneyStack}
          options={{ headerShown: false }}
        />

        <Stack.Screen
          name='aiExperience'
          component={AIExperienceStack}
          options={{ headerShown: false }}
        />

        <Stack.Screen
          name='settings'
          component={SettingsScreen}
          options={{
            title: getRouteTitle('settings'),
            headerShown: true,
          }}
        />

        <Stack.Screen
          name='helpSupport'
          component={HelpSupportScreen}
          options={{
            title: getRouteTitle('helpSupport'),
            headerShown: true,
          }}
        />

        <Stack.Screen
          name='dashboardTest'
          component={DashboardTestScreen}
          options={{ headerShown: false }}
        />
      </Stack.Navigator>
    );
  }, [isSmallScreen, MobileNavigator, LargeScreenTabNavigator]);

  // Wrap entire navigator in ResponsiveLayout
  return (
    <ResponsiveLayout
      activeRoute={activeRoute}
      onNavigate={handleNavigate}
      showSidebar={!isSmallScreen}
    >
      <CoreNavigator />
    </ResponsiveLayout>
  );
}

/**
 * MainNavigator - Production-Ready with All Fixes
 *
 * Features:
 * - Clean URLs without /index suffix
 * - Proper header rendering without content overlap
 * - Responsive bottom tabs (hidden on large screens ≥768px)
 * - Sidebar navigation on desktop
 * - Optimized performance with memoization
 * - Proper TypeScript integration
 */
export function MainNavigator() {
  return <NavigationContent />;
}
